//Built-in libraries

//Third party libraries
import { PlusIcon } from '@heroicons/react/24/outline';
import { type ChangeEvent, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import Input from '@/components/UI/Form/Input';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';

//STP Libraries

//Assets and Miscellaneous
import { initialValues } from './constants';
import type { IMember } from './types';

//Component Begin

const BoardMembers = ({ formValues }: { formValues: any }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editMemberId, setEditMemberId] = useState<string>('');
  const [selectedMember, setSelectedMember] = useState<IMember>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [member, setMember] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  const resetForm = () => {
    setMember(initialValues);
  };

  const editMemberHandler = (f: any) => {
    setIsEditing(true);
    const formData = Object.keys(member).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...member[next],
          value: f[next],
          isTouched: !!f[next],
        },
      };
    }, {});
    setMember(formData);
    setEditMemberId(f.id);
  };

  const updateMemberHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(member).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: member[next].value,
            id: editMemberId,
          };
        },
        {}
      );
      updateData({
        ...data,
        step4: {
          ...formValues,
          boardMembers: data.step4.boardMembers.map((p: any) => {
            if (p.id === editMemberId) {
              return formattedData;
            }
            return p;
          }),
        },
      });
      resetForm();
      setIsEditing(false);
    }
  };

  const removeMemberHandler = (member: IMember) => {
    setShowRemoveConfirm(true);
    setSelectedMember(member);
  };

  const confirmRemoveMember = async () => {
    setShowRemoveConfirm(false);
    const filteredMembers = data.step4.boardMembers.filter((member: any) => member.id !== selectedMember?.id);
    updateData({
      ...data,
      step4: {
        ...formValues,
        boardMembers: filteredMembers,
      },
    });

    if (selectedMember?.id === editMemberId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveMember = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...member };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setMember((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddMemberHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(member).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: member[next].value };
        },
        {}
      );
      updateData({
        ...data,
        step4: {
          ...data.step4,
          boardMembers: [...data.step4.boardMembers, { ...formattedData, id: uuidv4() }],
        },
      });
      resetForm();
    }
  };

  const inputChangedHandler = createInputChangedHandler(setMember, member);

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove member'
          message={`You are about to remove ${selectedMember?.name} member. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveMember}
          decline={cancelRemoveMember}
        />
      )}
      <section className='space-y-4'>
        <h3>Board Members</h3>
        {data.step4?.boardMembers.length > 0 && (
          <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter overflow-hidden'>
            <table className='divide-y divide-gray-200 w-full'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Name</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Email</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Company</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'></th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {data.step4.boardMembers.map((member: IMember) => (
                  <tr key={member.id} className={`${member.id === editMemberId ? 'bg-neutralColor-lighter' : ''}`}>
                    <td className='p-4'>{member.name}</td>
                    <td className='p-4'>{member.email}</td>
                    <td className='p-4'>{member.company}</td>
                    <td className='flex justify-end gap-4 p-4'>
                      <div className='cursor-pointer hover:text-blue-500' onClick={() => editMemberHandler(member)}>
                        Edit
                      </div>
                      <div className='cursor-pointer hover:text-tertiaryColor-normal' onClick={() => removeMemberHandler(member)}>
                        Delete
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className='w-full grid grid-cols-6 gap-4'>
          <Input
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Name'
            id='name'
            name='name'
            type='text'
            value={member.name.value}
            error={member?.name?.error}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
          />
          <Input
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Email'
            id='email'
            name='email'
            type='text'
            value={member.email.value}
            error={member?.email?.error}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
          />
          <Input
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Company'
            id='company'
            name='company'
            type='text'
            value={member.company.value}
            error={member?.company?.error}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
          />
        </div>
        {!isEditing && <Button icon={<PlusIcon />} label='Add' type='button' btnStyle='primary' onClick={onAddMemberHandler} />}
        {isEditing && (
          <div className='flex gap-4'>
            <Button label='Save' type='button' btnStyle='primary' onClick={updateMemberHandler} />
            <Button
              label='Cancel'
              type='button'
              btnStyle='neutral'
              onClick={() => {
                resetForm();
                setEditMemberId('');
                setIsEditing(false);
              }}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default BoardMembers;
